/** The Morning Nob (M16, FR-7.3). Split from the api.ts monolith 2026-08-03; the barrel re-exports. */

import { formatDuration } from "../format";
import { bareHandle, call, get, nullableNum, num, optTextOf, patch, strings } from "./core";
import { type InboxBand, INBOX_BANDS } from "./inbox";
import { type KnockReadFailureCode, knockReadFailure } from "./knock";
// --- the Morning Nob (M16, PRD FR-7.3 / §2.3) -------------------------------

/**
 * The recap — the primary recipient surface (§2.3). The inbox is the drill-down;
 * this is the product, the thing a door owner lands on. Everything below
 * normalises as tolerantly as the inbox above: the contract is pinned but the
 * API is still moving, and a missing optional field must never blank the
 * briefing. Insights are GROUNDED (FR-7.4) — an insight the server did not
 * phrase is never shown, so a line with no text is dropped, never invented.
 */

export type RecapSender = { handle: string; display_name: string; company_url: string | null };

/** The band the recipient reads plainly — the same set the inbox allows. */
export type RecapStanding = { band: InboxBand; percentile: number | null };

export type RecapReceipts = {
  claims_verified: number;
  claims_refuted: number;
  pages_reviewed: number;
  sources_checked: number;
};

/**
 * One knock that cleared the bar (FR-7.3). `summary` is the why-surfaced
 * one-liner — the reason it made the cut, in Nob's words. The receipts are the
 * dry evidence line.
 */
export type RecapItem = {
  knock_id: string;
  position: number;
  sender: RecapSender;
  standing: RecapStanding;
  summary: string;
  receipts: RecapReceipts;
  has_booking: boolean;
  /**
   * The owner has already answered this one. The card is a summary now (owner,
   * 2026-08-26) — it carries no verbs — so the one thing it still has to say
   * about state is whether the door is open, and this is that fact. From the
   * server's `action.replied`, which is the live `recipient_actions` read.
   */
  replied: boolean;
};

/**
 * A grounded line about the door (FR-7.4): phrased by the model, mapped to a
 * computed aggregate, never invented. `proposal_id` links the one-tap action to
 * a House Rules proposal (M15) — present when there is something to accept or
 * dismiss, `null` when the line is only telling the recipient something.
 */
export type Insight = {
  id: string;
  kind: string;
  text: string;
  proposal_id: string | null;
  action_label: string | null;
};

export type Recap = {
  date: string;
  /** The server's greeting, or `null` — the screen says "Morning, big nob." then. */
  greeting: string | null;
  surfaced_count: number;
  total_knocks: number;
  filtered_count: number;
  seconds_saved: number;
  /** "3h 42m" — the value made visible (FR-7.3). Derived here only if the server omits it. */
  time_saved_label: string;
  /**
   * What the counts cover, in the server's words — "Since yesterday" for the
   * overlay's day window, "Since your last briefing" for an emailed digest's.
   * `""` when the server did not say, which is the stored recap: a count with no
   * window is still a count, and this file invents nothing.
   */
  window_label: string;
  items: RecapItem[];
  insights: Insight[];
  /** Silence is a feature (FR-7.5): nothing cleared the bar, so there is nothing to show. */
  suppressed: boolean;
  /**
   * DOORS THAT OPENED FOR THIS PERSON AND THAT THEY HAVE NOT BEEN INTO
   * (ruling R-0910-7 rule 1) — the one fact in this briefing that is about the
   * reader as a SENDER rather than about their door. It takes the headline
   * when it is not zero: the owner's words were "a door that opened is one of
   * the most important things that are worth waking you for".
   */
  doors_opened: number;
  /** The newest of them — where the banner's tap goes. */
  doors_opened_knock_id: string | null;
};

function recapBandOf(value: unknown): InboxBand {
  return typeof value === "string" && (INBOX_BANDS as readonly string[]).includes(value)
    ? (value as InboxBand)
    : "MIDDLE";
}

function normaliseRecapItem(raw: unknown, index: number): RecapItem | null {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as Record<string, unknown>;
  const id =
    typeof b["knock_id"] === "string" && b["knock_id"]
      ? b["knock_id"]
      : typeof b["id"] === "string"
        ? b["id"]
        : "";
  if (!id) return null; // an item with no knock is one nothing can act on

  const senderRaw = (b["sender"] && typeof b["sender"] === "object" ? b["sender"] : {}) as Record<
    string,
    unknown
  >;
  const standingRaw = (
    b["standing"] && typeof b["standing"] === "object" ? b["standing"] : {}
  ) as Record<string, unknown>;
  const receiptsRaw = (
    b["receipts"] && typeof b["receipts"] === "object" ? b["receipts"] : {}
  ) as Record<string, unknown>;

  const handle = typeof senderRaw["handle"] === "string" ? bareHandle(senderRaw["handle"]) : "";
  const display =
    typeof senderRaw["display_name"] === "string" && senderRaw["display_name"].trim()
      ? (senderRaw["display_name"] as string)
      : handle
        ? `@${handle}`
        : "";
  const company =
    typeof senderRaw["company_url"] === "string" && senderRaw["company_url"].trim()
      ? (senderRaw["company_url"] as string)
      : null;
  // The server's names (recap/build.ts `RecapItemJson`): `why_surfaced` is the
  // FR-7.3 grounding and carries the item, and the recipient brief stands in
  // where there is none. This parser once read `summary`, `ask` and
  // `has_booking` — three fields of its own invention, so the why-line degraded
  // to the recipient brief and Book never rendered. (The "ask" line read a
  // one-line summary no hearing ever wrote; it is gone with it, R-0927-1.)
  const why = optTextOf(b["why_surfaced"]);
  const summary = why ?? optTextOf(b["summary_for_recipient"]) ?? "";
  const actionRaw = (b["action"] && typeof b["action"] === "object" ? b["action"] : {}) as Record<
    string,
    unknown
  >;
  return {
    knock_id: id,
    position: num(b["position"], index + 1),
    sender: { handle, display_name: display, company_url: company },
    standing: {
      band: recapBandOf(standingRaw["band"]),
      percentile: nullableNum(standingRaw["percentile"]),
    },
    summary,
    receipts: {
      claims_verified: num(receiptsRaw["claims_verified"]),
      claims_refuted: num(receiptsRaw["claims_refuted"]),
      pages_reviewed: num(receiptsRaw["pages_reviewed"]),
      sources_checked: num(receiptsRaw["sources_checked"]),
    },
    has_booking: b["has_booking_link"] === true,
    replied: actionRaw["replied"] === true,
  };
}

/** A line with no server phrasing is not grounded, so it is not an insight (FR-7.4). */
function normaliseInsight(raw: unknown): Insight | null {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as Record<string, unknown>;
  // The server's line is `phrasing` (recap/build.ts `RecapInsightJson`), and a
  // line with none is not grounded (FR-7.4). This once read `text` — a field of
  // this file's invention — so every insight the server sent was dropped here.
  const text = typeof b["phrasing"] === "string" ? b["phrasing"].trim() : "";
  if (!text) return null;
  const actionRaw = (b["action"] && typeof b["action"] === "object" ? b["action"] : {}) as Record<
    string,
    unknown
  >;
  const proposalId =
    typeof b["proposal_id"] === "string" && b["proposal_id"].trim()
      ? (b["proposal_id"] as string)
      : null;
  return {
    // The server sends no id of its own; the proposal (when there is one) or
    // the kind is the stable name the accept/dismiss state keys on.
    id: proposalId ?? (typeof b["kind"] === "string" ? b["kind"] : ""),
    kind: typeof b["kind"] === "string" ? b["kind"] : "",
    text,
    proposal_id: proposalId,
    action_label:
      typeof actionRaw["label"] === "string" && actionRaw["label"].trim()
        ? (actionRaw["label"] as string)
        : null,
  };
}

/** Exported for the seam test (`inbox-contract.test.ts`); reached via `fetchRecap`. */
export function normaliseRecap(raw: unknown): Recap {
  const b = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;

  const items: RecapItem[] = [];
  const rawItems = Array.isArray(b["items"]) ? b["items"] : [];
  rawItems.forEach((item, i) => {
    const normalised = normaliseRecapItem(item, i);
    if (normalised) items.push(normalised);
  });

  const insights: Insight[] = [];
  const rawInsights = Array.isArray(b["insights"]) ? b["insights"] : [];
  for (const item of rawInsights) {
    const insight = normaliseInsight(item);
    if (insight) insights.push(insight);
  }

  // The counts live under `summary` on the wire (recap/build.ts `RecapJson`).
  // Reading them at the top level — as this once did — made every count zero,
  // so the recap's whole boast ("N kept off your plate") never said anything.
  const summaryRaw = (
    b["summary"] && typeof b["summary"] === "object" ? b["summary"] : {}
  ) as Record<string, unknown>;
  const filtered = num(summaryRaw["filtered_count"]);
  // Time saved is knocks_filtered × 45s (FR-7.3). Derived only when the server
  // omits the label, so the value is never blank in front of the recipient.
  const secondsSaved = num(summaryRaw["seconds_saved"], filtered * 45);
  const label =
    typeof summaryRaw["time_saved_display"] === "string" && summaryRaw["time_saved_display"].trim()
      ? (summaryRaw["time_saved_display"] as string)
      : secondsSaved > 0
        ? formatDuration(secondsSaved)
        : "";

  return {
    date: typeof b["for_date"] === "string" ? b["for_date"] : "",
    greeting: optTextOf(b["greeting"]),
    surfaced_count: num(summaryRaw["surfaced_count"], items.length),
    total_knocks: num(summaryRaw["total_knocks"]),
    filtered_count: filtered,
    seconds_saved: secondsSaved,
    time_saved_label: label,
    window_label: optTextOf(summaryRaw["window_label"]) ?? "",
    items,
    insights,
    suppressed: b["suppressed"] === true,
    doors_opened: num(summaryRaw["doors_opened"]),
    doors_opened_knock_id: optTextOf(summaryRaw["doors_opened_knock_id"]),
  };
}

export type RecapResult = { ok: true; recap: Recap } | { ok: false; code: KnockReadFailureCode };

/**
 * `preview` is today's would-be recap — always current, which is why the home
 * surface reads it; `latest` is the last one actually sent. A suppressed morning
 * is a 200 with `suppressed: true`, not an error — silence is a feature (FR-7.5).
 */
export async function fetchRecap(kind: "preview" | "latest" = "preview"): Promise<RecapResult> {
  const r = await call(() => get(`/api/v1/recap/${kind}`));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return knockReadFailure(r.res.status);
  return { ok: true, recap: normaliseRecap(r.body) };
}

/**
 * The delivery configuration (FR-7.1) — cap, cadence and hour, the three the
 * recipient can nudge from here; the threshold, quiet days and enabled flag are
 * read for context. `threshold` is natural-language in House Rules, so it is
 * coerced to a string rather than assumed a number.
 */
export type RecapConfig = {
  cap: number;
  cadence: string;
  hour: number;
  threshold: string;
  quiet_days: string[];
  enabled: boolean;
  /** `none` / `matching` / `every` — the alerts half of the contract (R-0910-3). */
  alert_mode: string;
};

export type RecapConfigResult =
  | { ok: true; config: RecapConfig; warning: string | null }
  | { ok: false; code: "UNAUTHENTICATED" | "UNKNOWN" };

function normaliseRecapConfig(raw: unknown): RecapConfig {
  const b = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  // PATCH answers with the config nested under `config`; GET answers flat. Read
  // whichever is there so both entry points land on the same shape.
  const c =
    b["config"] && typeof b["config"] === "object" ? (b["config"] as Record<string, unknown>) : b;
  const threshold =
    typeof c["threshold"] === "string"
      ? c["threshold"]
      : typeof c["threshold"] === "number"
        ? String(c["threshold"])
        : "";
  return {
    cap: num(c["cap"], 5),
    cadence: typeof c["cadence"] === "string" && c["cadence"] ? c["cadence"] : "daily",
    hour: num(c["hour"], 8),
    threshold,
    quiet_days: strings(c["quiet_days"]),
    enabled: c["enabled"] !== false,
    alert_mode: typeof c["alert_mode"] === "string" ? c["alert_mode"] : "matching",
  };
}

/** The filtering-ratio nudge (FR-7.2), when there is one — a warning, never a block. */
function warningOf(raw: unknown): string | null {
  if (!raw || typeof raw !== "object") return null;
  return optTextOf((raw as Record<string, unknown>)["warning"]);
}

export async function fetchRecapConfig(): Promise<RecapConfigResult> {
  const r = await call(() => get("/api/v1/recap/config"));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return { ok: false, code: r.res.status === 401 ? "UNAUTHENTICATED" : "UNKNOWN" };
  return { ok: true, config: normaliseRecapConfig(r.body), warning: warningOf(r.body) };
}

export type RecapConfigInput = { cap?: number; cadence?: string; hour?: number };

/**
 * Nudge, never block (FR-7.2): the change is saved whatever the ratio, and any
 * warning comes back alongside the saved config for the screen to state dryly.
 */
export async function updateRecapConfig(input: RecapConfigInput): Promise<RecapConfigResult> {
  const body: Record<string, unknown> = {};
  if (typeof input.cap === "number") body["cap"] = input.cap;
  if (typeof input.cadence === "string") body["cadence"] = input.cadence;
  if (typeof input.hour === "number") body["hour"] = input.hour;
  const r = await call(() => patch("/api/v1/recap/config", body));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return { ok: false, code: r.res.status === 401 ? "UNAUTHENTICATED" : "UNKNOWN" };
  return { ok: true, config: normaliseRecapConfig(r.body), warning: warningOf(r.body) };
}
